import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { getPageRoute } from 'helpers/Utils';
import {
  sizes, Table, HtmlParser, Link,
} from '@hyphengroup/cag-components';
import withApplicationConfig from '../../containers/withApplicationConfig';

const Wrapper = styled.div`
  margin: ${sizes(10)} auto;
  max-width: ${sizes(150)};
  padding: 0 ${sizes(3)};
  p {
    margin: 0;
  }
`;

const supportedVerticals = {
  ba: 'PAYROLL_ACCOUNT',
  cc: 'CREDIT_CARDS',
  hi: 'HOME_INSURANCE',
  hl: 'MORTGAGES',
  mi: 'DOMESTIC_HELPER_INSURANCE',
  pl: 'PERSONAL_LOANS',
  sa: 'SECURITIES_ACCOUNT',
  ip: 'INVESTMENT',
  ti: 'TRAVEL_INSURANCE',
};

const SeoTable = ({
  data,
  tableHeading,
  columnHeadings,
  firstColumnIsProductLink,
  language,
  ccProductPageRoutes,
  providerProductPageRoute,
  mappings,
}) => {
  if (!data) {
    return null;
  }
  const renderComparable = (comparable) => (
    <>
      {comparable.prefix}
      <HtmlParser>{comparable.displayValue}</HtmlParser>
      {comparable.suffix}
      <div>{comparable.subValue}</div>
    </>
  );

  const getLink = (product, vertical) => {
    const alias = product?.functional?.metaData?.alias;
    const providerAlias = product?.provider?.alias;
    const verticalRoute = mappings[supportedVerticals[vertical]].route;
    if (alias) {
      return getPageRoute(alias, language, verticalRoute, ccProductPageRoutes);
    }
    return getPageRoute(providerAlias, language, vertical, providerProductPageRoute);
  };

  const seoTableData = data ? {
    title: tableHeading,
    header: columnHeadings.map((heading) => ({ node: heading })),
    rows: Object
      .entries(data)
      .filter(e => e[1] && Object.keys(supportedVerticals).includes(e[0]))
      .map(([vertical, dataEntry]) => dataEntry.list?.products
        ?.map(product => product?.comparables
          ?.map((comparable, comparableIndex) => (
            ({
              node: comparableIndex === 0 && firstColumnIsProductLink
                ? (
                  <Link
                    base="none"
                    color="inherit"
                    dataSelectorId={`seoTableLink-${product.id}`}
                    to={getLink(product, vertical)}
                  >
                    {renderComparable(comparable)}
                  </Link>
                )
                : renderComparable(comparable),
            })
          )))).reduce((a, c, i) => i === 0),
  } : undefined;
  return (
    <Wrapper>
      <Table {...seoTableData} />
    </Wrapper>
  );
};

const SeoTableProductProps = PropTypes.shape({
  list: PropTypes.shape({
    products: PropTypes.arrayOf(PropTypes.shape({
      comparables: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        suffix: PropTypes.string,
        prefix: PropTypes.string,
        tooltip: PropTypes.string,
        displayValue: PropTypes.string,
        subValue: PropTypes.string,
        description: PropTypes.string,
      })),
      functional: PropTypes.shape({
        metaData: PropTypes.shape({
          alias: PropTypes.string,
        }),
      }),
      provider: PropTypes.shape({
        alias: PropTypes.string,
      }),
    })),
  }),
});

SeoTable.propTypes = {
  tableHeading: PropTypes.string.isRequired,
  columnHeadings: PropTypes.arrayOf(PropTypes.string).isRequired,
  firstColumnIsProductLink: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    ba: { ...SeoTableProductProps },
    cc: { ...SeoTableProductProps },
    hi: { ...SeoTableProductProps },
    hl: { ...SeoTableProductProps },
    mi: { ...SeoTableProductProps },
    pl: { ...SeoTableProductProps },
    sa: { ...SeoTableProductProps },
    ip: { ...SeoTableProductProps },
    ti: { ...SeoTableProductProps },
  }).isRequired,
  language: PropTypes.string.isRequired,
  ccProductPageRoutes: PropTypes.string.isRequired,
  providerProductPageRoute: PropTypes.string.isRequired,
  mappings: PropTypes.shape({}).isRequired,
};

export default withApplicationConfig(SeoTable, ['routes', 'verticals']);
